import { useMemo } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { queries } from '@/data';
import { useMapStore } from '@/features/map-core';
import { useAllSavedIstats } from '@/features/shortlist';
import { useSelectedSets } from './store';

interface ProvinceBeacon {
  prov: string;
  count: number;
  lat: number;
  lon: number;
}

const ZOOM_THRESHOLD = 10;

function makeIcon(count: number): L.DivIcon {
  return L.divIcon({
    html: `<div style="
      background: #fbbf24;
      color: #78350f;
      border: 2px solid #d97706;
      border-radius: 9999px;
      min-width: 28px;
      height: 28px;
      padding: 0 8px;
      font-size: 12px;
      font-weight: 700;
      line-height: 24px;
      text-align: center;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      white-space: nowrap;
    ">${count}</div>`,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export function BeaconPins() {
  const zoom = useMapStore((s) => s.zoom);
  // Union of explicitly selected comuni (map selection) + all saved shortlist comuni.
  const placeSets = useSelectedSets();
  const savedIstats = useAllSavedIstats();
  const comuniSet = useMemo(() => {
    if (savedIstats.size === 0) return placeSets.comuni;
    const merged = new Set(placeSets.comuni);
    for (const istat of savedIstats) merged.add(istat);
    return merged;
  }, [placeSets.comuni, savedIstats]);

  const { data: meta } = useQuery(queries.meta());
  const version = meta?.version ?? '';
  const { data: comuni } = useQuery({
    ...queries.full(version),
    enabled: !!version && comuniSet.size > 0,
  });

  const beacons = useMemo<ProvinceBeacon[]>(() => {
    if (!comuni || comuniSet.size === 0) return [];
    const byProv = new Map<string, { count: number; latSum: number; lonSum: number }>();
    for (const c of comuni) {
      if (!comuniSet.has(c.istat)) continue;
      const acc = byProv.get(c.prov) ?? { count: 0, latSum: 0, lonSum: 0 };
      acc.count += 1;
      acc.latSum += c.lat;
      acc.lonSum += c.lon;
      byProv.set(c.prov, acc);
    }
    return Array.from(byProv.entries()).map(([prov, { count, latSum, lonSum }]) => ({
      prov,
      count,
      lat: latSum / count,
      lon: lonSum / count,
    }));
  }, [comuni, comuniSet]);

  if (zoom >= ZOOM_THRESHOLD) return null;
  if (beacons.length === 0) return null;

  return (
    <>
      {beacons.map((b) => (
        <Marker
          key={b.prov}
          position={[b.lat, b.lon]}
          icon={makeIcon(b.count)}
          interactive={false}
        />
      ))}
    </>
  );
}
